import { Sigma } from "lucide-react";
import { useI18nContext } from "~/i18n/i18n-react";
import { STTDomain } from "~/utils/signalr-types";

import { ToggleCard } from "../ui/toggle-card";

interface MathDomainToggleProps {
	domain: STTDomain;
	onDomainChange: (domain: STTDomain) => void;
	/** Whether the currently selected model accepts a domain at all. */
	isSupported: boolean;
	/** The room's model is locked, so nobody may change the domain. */
	isLocked?: boolean;
}

/**
 * Math Optimized switch. Stays visible for every model and greys out when the
 * selected one cannot use a domain, so the option never disappears mid-flow.
 */
export const MathDomainToggle = ({
	domain,
	onDomainChange,
	isSupported,
	isLocked = false,
}: MathDomainToggleProps) => {
	const { LL } = useI18nContext();

	const disabledHint = isLocked
		? LL.TRANSCRIPTION_SETTINGS.MODEL_LOCKED_HINT()
		: LL.TRANSCRIPTION_SETTINGS.DOMAIN_MATH_UNAVAILABLE();

	return (
		<ToggleCard
			// Shows the effective state: a model that cannot use a domain reads as
			// off, while a locked room still shows the domain the host chose.
			checked={isSupported && domain === STTDomain.Math}
			description={LL.TRANSCRIPTION_SETTINGS.DOMAIN_MATH_DESC()}
			disabled={!isSupported || isLocked}
			disabledHint={disabledHint}
			icon={<Sigma className="h-3.5 w-3.5" />}
			onCheckedChange={(checked) =>
				onDomainChange(checked ? STTDomain.Math : STTDomain.None)
			}
			title={LL.TRANSCRIPTION_SETTINGS.DOMAIN_MATH()}
		/>
	);
};
